import { useState, forwardRef, useEffect } from 'react';
import { Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import type { FormField, FormFieldType, UpdateFormFieldInput, RatingFieldOptions } from '../../types/forms';
import { FIELD_TYPE_LABELS } from '../../types/forms';

interface FieldEditorProps {
  field: FormField;
  isFirst: boolean;
  isLast: boolean;
  isFocused?: boolean;
  onUpdate: (fieldId: string, data: UpdateFormFieldInput) => void;
  onDelete: (fieldId: string) => void;
  onMoveUp: (fieldId: string) => void;
  onMoveDown: (fieldId: string) => void;
}

export const FieldEditor = forwardRef<HTMLDivElement, FieldEditorProps>(function FieldEditor({
  field,
  isFirst,
  isLast,
  isFocused,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
}, ref) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleChange = (key: keyof UpdateFormFieldInput, value: unknown) => {
    onUpdate(field.id, { [key]: value });
  };

  const showSelectOptions = field.type === 'SELECT' || field.type === 'MULTISELECT';
  const showRatingOptions = field.type === 'RATING';

  // Helper to get rating options
  const getRatingOptions = (): RatingFieldOptions => {
    if (field.options && typeof field.options === 'object' && !Array.isArray(field.options)) {
      return field.options as RatingFieldOptions;
    }
    return { label_low: '', label_high: '' };
  };

  const handleRatingOptionChange = (key: keyof RatingFieldOptions, value: string) => {
    const currentOptions = getRatingOptions();
    onUpdate(field.id, {
      options: { ...currentOptions, [key]: value || undefined }
    });
  };

  // Auto-expand when focused
  useEffect(() => {
    if (isFocused) {
      setIsExpanded(true);
    }
  }, [isFocused]);

  return (
    <div
      ref={ref}
      className={`bg-white dark:bg-slate-800 border rounded-xl overflow-hidden transition-all duration-300 ${
        isFocused
          ? 'border-primary ring-2 ring-primary/30 shadow-lg'
          : 'border-gray-200 dark:border-slate-700'
      }`}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-slate-700 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <GripVertical className="w-4 h-4 text-gray-400 dark:text-slate-500" />
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-white">{field.label || 'Neues Feld'}</p>
          <p className="text-xs text-gray-500 dark:text-slate-400">{FIELD_TYPE_LABELS[field.type]}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp(field.id);
            }}
            disabled={isFirst}
            className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown(field.id);
            }}
            disabled={isLast}
            className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(field.id);
            }}
            className="p-1.5 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Bezeichnung
              </label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => handleChange('label', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                placeholder="z.B. Vor- und Nachname"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Feldtyp
              </label>
              <select
                value={field.type}
                onChange={(e) => handleChange('type', e.target.value as FormFieldType)}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
              Platzhalter (optional)
            </label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={(e) => handleChange('placeholder', e.target.value || null)}
              className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
              placeholder="z.B. Max Mustermann"
            />
          </div>

          {showSelectOptions && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Optionen (eine pro Zeile)
              </label>
              <textarea
                value={Array.isArray(field.options) ? field.options.join('\n') : ''}
                onChange={(e) =>
                  handleChange(
                    'options',
                    e.target.value.split('\n').filter((o) => o.trim())
                  )
                }
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            </div>
          )}

          {showRatingOptions && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Label für 1 Stern (optional)
                  </label>
                  <input
                    type="text"
                    value={getRatingOptions().label_low || ''}
                    onChange={(e) => handleRatingOptionChange('label_low', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                    placeholder="z.B. Schlecht"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Label für 5 Sterne (optional)
                  </label>
                  <input
                    type="text"
                    value={getRatingOptions().label_high || ''}
                    onChange={(e) => handleRatingOptionChange('label_high', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                    placeholder="z.B. Ausgezeichnet"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Diese Labels werden unter der Bewertungsskala angezeigt.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`required-${field.id}`}
              checked={field.required}
              onChange={(e) => handleChange('required', e.target.checked)}
              className="w-4 h-4 text-primary border-gray-300 dark:border-slate-600 rounded focus:ring-primary bg-white dark:bg-slate-700"
            />
            <label
              htmlFor={`required-${field.id}`}
              className="text-sm text-gray-700 dark:text-slate-300"
            >
              Pflichtfeld
            </label>
          </div>
        </div>
      )}
    </div>
  );
});
